
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { renderEditableTextCell } from './renderers/KolDictionaryCellRenderers';
import { Twitter, Instagram, Youtube, MessageCircle, Globe, ExternalLink } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface CreatorInfoProps {
  name: string;
  handler?: string;
  platform?: string;
  region?: string;
  profileImageUrl?: string | null;
  editable?: boolean;
  onNameChange?: (newName: string) => void;
  isPending?: boolean;
  isOptimisticProcessing?: boolean;
  language?: string;
  compact?: boolean;
  hideBadges?: boolean;
}

export function CreatorInfo({
  name,
  handler,
  platform,
  region,
  profileImageUrl,
  editable = false,
  onNameChange,
  isPending,
  isOptimisticProcessing,
  language,
  compact = false,
  hideBadges = false
}: CreatorInfoProps) {
  // Get auth context to double-check BU access
  const { hasBuAccess } = useAuth();
  
  // HARD BLOCK: If user has BU access, force disable editing regardless of props
  const forceDisableForBuAccess = hasBuAccess;
  const finalEditable = editable && !forceDisableForBuAccess;
  const finalOnNameChange = forceDisableForBuAccess ? undefined : onNameChange;

  const getInitials = (name: string) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getPlatformIcon = (platform?: string) => {
    const iconProps = { className: compact ? "h-2 w-2" : "h-3 w-3" };
    
    switch (platform?.toLowerCase()) {
      case 'twitter':
      case 'x':
        return <Twitter {...iconProps} className={`${iconProps.className} text-blue-500`} />;
      case 'instagram':
        return <Instagram {...iconProps} className={`${iconProps.className} text-pink-500`} />;
      case 'youtube':
        return <Youtube {...iconProps} className={`${iconProps.className} text-red-500`} />;
      case 'telegram':
        return <MessageCircle {...iconProps} className={`${iconProps.className} text-blue-600`} />;
      default:
        return <Globe {...iconProps} className={`${iconProps.className} text-gray-500`} />;
    }
  };

  const getSocialMediaUrl = (platform?: string, handler?: string) => {
    if (!handler) return null;
    
    const cleanHandler = handler.startsWith('@') ? handler.slice(1) : handler;
    
    switch (platform?.toLowerCase()) {
      case 'twitter':
      case 'x':
        return `https://twitter.com/${cleanHandler}`;
      case 'instagram':
        return `https://instagram.com/${cleanHandler}`;
      case 'youtube':
        return `https://youtube.com/@${cleanHandler}`;
      case 'telegram':
        return `https://t.me/${cleanHandler}`;
      default:
        return null;
    }
  };

  const getRegionColor = (region?: string) => {
    switch (region?.toLowerCase()) {
      case 'global':
        return 'bg-green-100 text-green-800';
      case 'asia':
        return 'bg-yellow-100 text-yellow-800';
      case 'europe':
        return 'bg-blue-100 text-blue-800';
      case 'america':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLanguageColor = (language?: string) => {
    switch (language?.toLowerCase()) {
      case 'english':
        return 'bg-blue-100 text-blue-800';
      case 'chinese':
        return 'bg-red-100 text-red-800';
      case 'spanish':
        return 'bg-orange-100 text-orange-800';
      case 'french':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const avatarSize = compact ? 'h-6 w-6' : 'h-8 w-8';
  const textSize = compact ? 'text-xs' : 'text-sm';
  const badgeSize = compact ? 'text-xs px-1 py-0 h-3' : 'text-xs px-1 py-0 h-4';

  const socialMediaUrl = getSocialMediaUrl(platform, handler);

  const hasValidProfileImage = profileImageUrl && profileImageUrl.trim() !== '';

  // Determine if name should actually be editable with hard BU access check
  const isNameEditable = finalEditable && finalOnNameChange;

  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <Avatar className={`${avatarSize} flex-shrink-0`}>
          {hasValidProfileImage ? (
            <AvatarImage 
              src={profileImageUrl} 
              alt={name}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <AvatarFallback className={compact ? 'text-xs' : 'text-xs'}>
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        {platform && (
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 border border-gray-200">
            {getPlatformIcon(platform)}
          </div>
        )}
      </div>

      <div className="flex flex-col min-w-0 flex-1">
        {isNameEditable ? (
          <div className={`font-medium ${textSize}`}>
            {renderEditableTextCell({
              row: { id: name, creator_name: name },
              field: 'creator_name',
              placeholder: "Enter name...",
              isPending: () => isPending || false,
              applyOptimisticUpdate: (id, field, value) => finalOnNameChange(value),
              isOptimisticProcessing: isOptimisticProcessing || false
            })}
          </div>
        ) : (
          <div className={`font-medium ${textSize} truncate`} title={name}>
            {name}
          </div>
        )}

        {!hideBadges && (
          <div className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-xs'} text-muted-foreground`}>
            {handler && (
              <div className="flex items-center gap-1">
                <span className="truncate" title={handler}>
                  {handler.startsWith('@') ? handler : `@${handler}`}
                </span>
                {socialMediaUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => window.open(socialMediaUrl, '_blank')}
                  >
                    <ExternalLink className="h-2 w-2 text-muted-foreground hover:text-primary" />
                  </Button>
                )}
              </div>
            )}
            {region && (
              <Badge
                variant="outline"
                className={`${getRegionColor(region)} border-0 ${badgeSize}`}
              >
                {region}
              </Badge>
            )}
            {language && (
              <Badge
                variant="outline"
                className={`${getLanguageColor(language)} border-0 ${badgeSize}`}
              >
                {language}
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
